import type { ProjectMember } from '@/lib/api/project-members.api'
import { memberApi } from '@/lib/api/members.api'
import { projectMemberApi } from '@/lib/api/project-members.api'
import { readCompletePages } from '@/lib/query/read-complete-pages'

import type { Member } from '@scrambl/shared'

export interface AssignableProjectMember {
  id: string
  userId: string | null
  memberId: string | null
  name: string
  email: string
  avatarUrl?: string
  role?: string
  status: string
}

function displayName(profile: {
  displayName?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}) {
  const fullName = [profile.firstName, profile.lastName]
    .filter(Boolean)
    .join(' ')
    .trim()
  const label = profile.displayName?.trim()
  return (
    (label && label !== profile.email?.split('@')[0]
      ? label
      : fullName || label) ||
    profile.email?.split('@')[0] ||
    'Unknown User'
  )
}

function projectOption(member: ProjectMember): AssignableProjectMember {
  // Server may synthesize a profile for a member with no login. Keep that
  // member identity without inventing a user identity for assignment writes.
  const userId =
    member.userId ||
    (member.user?.id !== member.memberId ? member.user?.id : null) ||
    null
  const memberId = member.memberId || null
  return {
    id: userId || memberId || member.id,
    userId,
    memberId,
    status: member.status,
    name: displayName(member.user ?? {}),
    email: member.user?.email || '',
    avatarUrl: member.user?.image || undefined,
  }
}

function organizationOption(member: Member): AssignableProjectMember {
  const profile = {
    ...member,
    ...member.user,
    firstName: member.user?.firstName || member.firstName,
    lastName: member.user?.lastName || member.lastName,
    email: member.user?.email || member.email,
  }
  const userId = member.userId || member.user?.id || null
  return {
    id: userId || member.id,
    userId,
    memberId: member.id,
    name: displayName(profile),
    email: profile.email || '',
    avatarUrl: profile.image || member.image || undefined,
    role: member.role,
    status: member.status,
  }
}

/** An empty or refused project roster never expands to the workspace directory. */
export async function getAssignableProjectMembers(
  projectId: string | undefined,
  signal: AbortSignal,
): Promise<AssignableProjectMember[]> {
  const rows = projectId
    ? (
        await readCompletePages(
          page =>
            projectMemberApi.getProjectMembers(
              projectId,
              { page, limit: 100, sortBy: 'createdAt', sortOrder: 'asc' },
              { signal },
            ),
          signal,
        )
      )
        .filter(
          member => member.status === 'ACTIVE' || member.status === 'PENDING',
        )
        .map(projectOption)
    : // /members/display is an unpaginated identity directory. Its metadata is
      // { total, skip, take }, not the project roster's page-based contract.
      (await readOrganizationDirectory(signal))
        .filter(
          member => member.status === 'ACTIVE' || member.status === 'PENDING',
        )
        .map(organizationOption)
  return [...new Map(rows.map(row => [row.id, row])).values()].sort(
    (a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
      a.id.localeCompare(b.id),
  )
}

async function readOrganizationDirectory(
  signal: AbortSignal,
): Promise<Member[]> {
  signal.throwIfAborted()
  const response = await memberApi.getMembersForDisplay({}, { signal })
  signal.throwIfAborted()
  if (!Array.isArray(response?.data))
    throw new Error('Member list is unavailable')
  return response.data
}
